import { ObjectType } from '$app-web/types';

import { getObjectLength, isObject } from './object';
import { getStringInCamelCase, getStringInSnakeCase } from './string';

export function pipe<T>(...fns: Array<(arg: T) => T>) {
    return (arg: T) => fns.reduce((acc, fn) => fn(acc), arg);
}

export function areValuesEqual<T>(first: T, second: T): boolean {
    if (isObject(first) && isObject(second)) {
        if (getObjectLength(first as ObjectType) === getObjectLength(second as ObjectType)) {
            return Object.entries(first as ObjectType).every(([key, value]) => {
                if (key in (second as ObjectType)) {
                    return areValuesEqual((second as ObjectType)[key], value);
                }

                return false;
            });
        } else {
            return false;
        }
    } else if (Array.isArray(first) && Array.isArray(second)) {
        if (first.length === second.length) {
            return (first as Array<unknown>).every((value) =>
                (second as Array<unknown>).some((item) => areValuesEqual(item, value))
            );
        } else {
            return false;
        }
    } else {
        return first === second;
    }
}

export function transformToCamelCase<T>(data: T, excludeKeys: Array<string> = []): T {
    if (isObject(data)) {
        return Object.entries(data as ObjectType).reduce((newObject, [key, value]) => {
            const convertedInnerObject = transformToCamelCase(value as T, excludeKeys);
            if (excludeKeys.includes(key)) {
                return { ...newObject, [key]: convertedInnerObject };
            }
            const camelCaseKey = getStringInCamelCase(key);
            return { ...newObject, [camelCaseKey]: convertedInnerObject };
        }, {}) as T;
    }
    if (Array.isArray(data)) {
        return (data as Array<T>).map((item) => transformToCamelCase(item, excludeKeys)) as T;
    }

    return data as T;
}

export function transformToSnakeCase<T>(data: T, excludeKeys: Array<string> = []): T {
    if (isObject(data)) {
        return Object.entries(data as ObjectType).reduce((newObject, [key, value]) => {
            const convertedInnerObject = transformToSnakeCase(value as T, excludeKeys);
            if (excludeKeys.includes(key)) {
                return { ...newObject, [key]: convertedInnerObject };
            }
            const snakeCaseKey = getStringInSnakeCase(key);
            return { ...newObject, [snakeCaseKey]: convertedInnerObject };
        }, {}) as T;
    }
    if (Array.isArray(data)) {
        return (data as Array<T>).map((item) => transformToSnakeCase(item as T, excludeKeys)) as T;
    }
    return data as T;
}

export function isSuperset(superset: unknown, subset: unknown): boolean {
    if (typeof superset !== typeof subset) {
        return false;
    }
    if (isObject(superset) && isObject(subset)) {
        return Object.entries(subset as ObjectType).every(([key, value]) => {
            if (key in (superset as ObjectType)) {
                return isSuperset((superset as ObjectType)[key], value);
            }
            return false;
        });
    }
    if (Array.isArray(superset) && Array.isArray(subset)) {
        return (subset as Array<unknown>).every((value) => (superset as Array<unknown>).some((item) => isSuperset(item, value)));
    }

    return superset === subset;
}

export function cloneDeeply<T>(data: T): T {
    if (isObject(data)) {
        return Object.entries(data as ObjectType).reduce((newObject, [key, value]) => {
            return { ...newObject, [key]: cloneDeeply(value) };
        }, {}) as T;
    }
    if (Array.isArray(data)) {
        return (data as Array<unknown>).map(cloneDeeply) as unknown as T;
    }
    return data;
}
